import React, { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import i18next from 'i18next';
import { isGerman } from '@/lib/language';
import { trackConversion } from '@/lib/analytics';
import { Spinner } from '@/components/ui/spinner';
import {
  isProfileFailureCode,
  readEdgeFunctionErrorBody,
  resolveOAuthErrorCopy,
  type OAuthErrorCopy,
} from '@/lib/oauthError';
import { adsConnectReturnPath, takeAdsConnectReturn } from '@/lib/adsConnectReturn';

const GoogleCallback: React.FC = () => {
  const { t } = useTranslation('common');
  const [searchParams] = useSearchParams();
  /**
   * Finding R3-3: sichtbarer Endzustand fuer den Fall ohne Opener (Popup geblockt, also
   * Vollbild-Weiterleitung). Bei GOOGLE_SCOPE_MISSING gibt es eine google_accounts-Zeile
   * und ein Banner, auf das man weiterleiten kann. Bei einem gescheiterten Profilabruf
   * existiert nichts: kein Konto, keine Session, kein Banner. Ein stilles
   * `replace('/google-accounts?oauth=error')` waere hier die alte Sackgasse mit neuem
   * Anstrich, deshalb erklaert sich diese Seite selbst.
   */
  const [failure, setFailure] = useState<OAuthErrorCopy | null>(null);
  /**
   * Googles Authorization Code ist EINMALIG. Ein zweiter Einlauf beantwortet Google mit
   * `invalid_grant`, der Handler wirft beim Token-Tausch und der Nutzer bekommt den
   * generischen 500, also exakt die Sackgasse, gegen die dieser Commit antritt.
   *
   * Seit R3-3 haengt der Effect zusaetzlich an `t`, weil er `t` benutzt. Heute loest das
   * nichts aus (react-i18next 16 liefert eine stabile Referenz, und diese Route rendert
   * ohne Layout, also ohne Sprachumschalter), aber ein einziges `languageChanged` hier
   * wuerde reichen. Statt die Dependency zu verschweigen und exhaustive-deps anzuluegen,
   * wird der Einlauf hart auf einmal begrenzt. Das ist ohnehin die Wahrheit ueber diese
   * Operation und schuetzt nebenbei gegen ein doppeltes Mounten unter StrictMode.
   */
  const redeemStartedRef = useRef(false);

  useEffect(() => {
    if (redeemStartedRef.current) return;
    redeemStartedRef.current = true;

    /**
     * Einziger Weg, auf dem ein erkannter Fehlercode diese Seite verlaesst.
     *
     * Popup-Fall: der Code reist mit, damit jeder Listener (GoogleOAuthButton,
     * ReauthDialog, Auth, ConnectGoogleStep) die passende Ueberschrift waehlen kann;
     * `error` traegt zusaetzlich den bereits uebersetzten Satz, damit ein Listener, der
     * den Code nicht kennt, trotzdem etwas Brauchbares anzeigt.
     *
     * Vollbild-Fall: fuer die R3-3-Codes bleiben wir hier und erklaeren es, weil es kein
     * Ziel gibt, das es erklaeren koennte. GOOGLE_SCOPE_MISSING behaelt seine
     * Weiterleitung aus R2-3, denn dort wartet das Banner mit dem Reparaturpfad.
     */
    const reportFailure = (copy: OAuthErrorCopy) => {
      if (window.opener) {
        window.opener.postMessage({
          type: 'GOOGLE_OAUTH_ERROR',
          code: copy.code,
          error: copy.description,
        }, window.location.origin);
        window.close();
        return;
      }

      if (isProfileFailureCode(copy.code)) {
        setFailure(copy);
        return;
      }

      window.location.replace('/google-accounts');
    };

    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      // Beide fruehen Fehlerzweige (Googles `error`-Param, z.B. access_denied
      // nach "Abbrechen" auf dem Consent-Screen, und fehlender code/state)
      // muessen den Vollbild-Fall behandeln: ohne Opener ist postMessage ein
      // No-Op und window.close() wird fuer navigierte Tabs ignoriert — der
      // Nutzer hinge sonst unbegrenzt am "Verbindung wird hergestellt"-Spinner.
      // Gleiches Muster wie R3-3: sichtbarer Fehler-Endzustand mit
      // Zurueck-Link; resolveOAuthErrorCopy fuer bekannte Codes, sonst die
      // generische Verbinden-Copy. Popup-Verhalten unveraendert.
      // Der Modus wird VOR den fruehen Fehlerzweigen bestimmt, nicht erst im
      // Erfolgspfad. Der `state` reist laut OAuth-Spec auch auf der
      // Fehler-Weiterleitung mit, steht hier also zur Verfuegung -- und ohne
      // diese Zeile bekaeme ein abgebrochener ADS-Consent den gemeinsamen
      // `GOOGLE_OAUTH_ERROR`-Typ. Den hoeren `GoogleOAuthButton` und
      // `ReauthDialog` mit: der Nutzer haette eine Fehlermeldung ueber seine
      // MERCHANT-Verbindung gesehen, weil er die ADS-Freigabe abgebrochen hat.
      const isAdsMode = typeof state === 'string' && state.startsWith('ads:');

      const failEarly = (rawError: string) => {
        if (window.opener) {
          window.opener.postMessage({
            type: isAdsMode ? 'GOOGLE_ADS_OAUTH_ERROR' : 'GOOGLE_OAUTH_ERROR',
            code: rawError,
            error: rawError,
          }, window.location.origin);
          window.close();
          return;
        }
        // Vollbild-Fall: im Ads-Modus gehoert die Meldung auf die Ads-Seite,
        // nicht in den generischen Verbinden-Endzustand. Ohne Marker ist das
        // interne Panel das Ziel (Connect von dort); mit Marker der Feed-Check
        // bzw. Settings, wo useAdsConnectFlow den Parameter adsOauth auswertet
        // (Spec 2026-09-09, 4.3).
        if (isAdsMode) {
          const target = takeAdsConnectReturn();
          window.location.replace(
            target
              ? adsConnectReturnPath(target, 'error', rawError)
              : `/internal/ads-performance?adsOauth=error&code=${encodeURIComponent(rawError)}`,
          );
          return;
        }
        // KEIN Ads-Modus: ein Marker aus einem frueheren Vollbild-Versuch
        // gehoert hier nicht mehr hin. Er beschreibt EINE Landung, und das
        // ist diese -- bliebe er liegen, leitete der naechste Connect aus
        // dem internen Panel auf den Feed-Check um.
        takeAdsConnectReturn();
        setFailure(
          resolveOAuthErrorCopy(rawError, t) ?? {
            code: rawError,
            title: t('oauth.connectionFailed'),
            description: t('oauth.connectionFailedDesc'),
          },
        );
      };

      if (error) {
        failEarly(error);
        return;
      }

      if (!code || !state) {
        failEarly('Missing authorization code or state');
        return;
      }

      try {
        // Parse mode from the state parameter.
        //
        // State-Formate:
        //   "auth:<nonce>"   -> Anmeldung ueber Google
        //   "ads:<userId>"   -> Verbinden des eigenen Google-Ads-Kontos (Spec AP3)
        //   "<userId>"       -> Verbinden des Merchant-Google-Kontos (Voreinstellung)
        //
        // Der Offset wird je Praefix HARTKODIERT abgeschnitten und muss zur
        // Praefixlaenge passen: "auth:" sind 5 Zeichen, "ads:" sind 4. Ein
        // gemeinsamer Wert waere hier der naechstliegende Fehler -- mit 5 auf
        // "ads:" bliebe vom ersten Zeichen der UUID nichts uebrig, und der
        // Handler faende schlicht kein Konto.
        const isAuthMode = state.startsWith('auth:');
        const isAdsMode = state.startsWith('ads:');
        const mode = isAuthMode ? 'auth' : isAdsMode ? 'ads' : 'connect';
        const stateValue = isAuthMode
          ? state.substring(5)
          : isAdsMode
            ? state.substring(4)
            : state;

        const redirectUri = `${window.location.origin}/auth/google/callback`;
        const { data, error: functionError } = await supabase.functions.invoke('google-oauth-handler', {
          // locale: die Sprache, in der sich ein NEUES Konto anmeldet, schreibt der
          // Handler als erste Profil- und Mailsprache fest (initial-language.ts).
          body: { code, state: stateValue, redirectUri, mode, locale: isGerman(i18next.language) ? 'de' : 'en' }
        });

        // ── Ads-Modus: eigener Abschluss in BEIDE Richtungen (Spec AP3) ──────
        //
        // Steht bewusst VOR der allgemeinen Fehlerbehandlung und damit auch vor
        // der GOOGLE_SCOPE_MISSING-Pruefung. Beide Wege darunter enden in
        // `reportFailure`, und das postet `GOOGLE_OAUTH_ERROR` -- einen Typ, auf
        // den GoogleOAuthButton und ReauthDialog mithoeren. Ein gescheiterter
        // Ads-Consent wuerde dort als kaputte Merchant-Verbindung getoastet,
        // waehrend der Ads-Knopf selbst weiterlaeuft, bis der Nutzer das Popup
        // von Hand schliesst.
        //
        // Ebenso wichtig: eine Ads-Antwort traegt NIE `GOOGLE_SCOPE_MISSING`
        // (der Handler laesst sie an withScopeGate vorbei), aber die Pruefung
        // darunter liest nur `data?.code` und wuesste den Unterschied nicht.
        if (mode === 'ads') {
          const errorBody = functionError ? await readEdgeFunctionErrorBody(functionError) : null;
          const succeeded = !functionError && data?.success === true;
          const failureCode = (errorBody?.code ?? data?.code ?? 'ADS_CONNECT_FAILED') as string;

          if (window.opener) {
            window.opener.postMessage(
              succeeded
                ? { type: 'GOOGLE_ADS_OAUTH_SUCCESS', data }
                : { type: 'GOOGLE_ADS_OAUTH_ERROR', code: failureCode },
              window.location.origin,
            );
            window.close();
            return;
          }

          // Vollbild-Fall (Popup blockiert). Ohne Marker ist das interne Panel
          // das Ziel (Connect von dort); mit Marker der Feed-Check bzw.
          // Settings, wo useAdsConnectFlow den Parameter adsOauth auswertet
          // (Spec 2026-09-09, 4.3). Der Code reist als Parameter mit, damit
          // dort nicht geraten werden muss.
          const target = takeAdsConnectReturn();
          window.location.replace(
            target
              ? adsConnectReturnPath(target, succeeded ? 'success' : 'error', failureCode)
              : succeeded
                ? '/internal/ads-performance'
                : `/internal/ads-performance?adsOauth=error&code=${encodeURIComponent(failureCode)}`,
          );
          return;
        }

        if (functionError) {
          // Finding R3-3: der Handler antwortet auf einen gescheiterten Profilabruf mit
          // 503/403/502 und einem `code` im Body. supabase-js macht daraus einen
          // FunctionsHttpError, dessen `message` immer "Edge Function returned a non-2xx
          // status code" lautet. Wuerden wir den nur weiterwerfen, saehe der Nutzer
          // genau diesen Satz und der ganze Fix waere verpufft.
          const body = await readEdgeFunctionErrorBody(functionError);
          const copy = resolveOAuthErrorCopy(body?.code, t);

          if (copy) {
            // Bewusst KEIN eigenes Sentry-Event: google-oauth-handler hat diesen Vorfall
            // gerade selbst gemeldet, mit mehr Kontext (upstreamStatus, Modus, Ursache).
            // Ein zweites, ungesampeltes Event aus dem Browser waere derselbe Vorfall
            // doppelt gezaehlt. Ein Breadcrumb reicht, damit ein spaeterer echter Fehler
            // in derselben Session die Vorgeschichte mitbringt.
            Sentry.addBreadcrumb({
              category: 'auth',
              level: 'warning',
              message: `google-oauth-handler failed: ${copy.code}`,
            });
            reportFailure(copy);
            return;
          }

          // Unbekannter Fehler: unveraendertes Verhalten, damit dieser Fix nichts
          // stillschweigend verschluckt, was er nicht versteht.
          throw functionError;
        }

        // Check if this is the Gmail setup flow
        if (data?.setupComplete) {
          const params = new URLSearchParams({
            success: 'true',
            setupComplete: 'true',
            email: data.tokens.FEEDOPTIMIZER_EMAIL,
            refreshToken: data.tokens.FEEDOPTIMIZER_GMAIL_REFRESH_TOKEN,
          });
          window.location.replace(`/admin/gmail-setup?${params.toString()}`);
          return;
        }

        // Auth mode: set session on client before posting message
        if (data?.mode === 'auth' && data?.session) {
          // If we have an opener, post the session data to it
          if (window.opener) {
            window.opener.postMessage({
              type: 'GOOGLE_AUTH_SUCCESS',
              data
            }, window.location.origin);
            window.close();
          } else {
            // Full-tab redirect fallback: set session directly and redirect
            await supabase.auth.setSession({
              access_token: data.session.access_token,
              refresh_token: data.session.refresh_token,
            });
            window.location.replace('/');
          }
          return;
        }

        // Finding R2-3: consent ran through, but Google did not return the Merchant API
        // scope, so the account cannot read a single product. Reported as an error, not
        // a success: the connect flow would otherwise close the popup, kick off
        // fetch-account-data against a blind token and toast "connected".
        //
        // Placed AFTER the auth branch on purpose. In auth mode this callback carries the
        // session that signs the user in for the very first time; turning that into an
        // error would leave them outside the app with no way to fix anything. There they
        // are logged in and the persistent banner offers the repair path instead.
        //
        // Seit R3-3 laeuft das ueber dieselbe reportFailure-Funktion wie die
        // Profilfehler: Popup -> postMessage mit Code, Vollbild -> Weiterleitung auf
        // /google-accounts, wo die Zeile mit auth_status='scope_missing' samt Banner
        // und Kontokarte wartet. Verhalten unveraendert, nur noch an einer Stelle.
        const scopeCopy = data?.code === 'GOOGLE_SCOPE_MISSING'
          ? resolveOAuthErrorCopy(data.code, t)
          : null;
        if (scopeCopy) {
          reportFailure(scopeCopy);
          return;
        }

        // Connect mode (default behavior)
        // Funnel-Event (Bericht 8.3 Punkt 6): der Handler hat den Account
        // gespeichert und alle Fehlerpfade (inkl. GOOGLE_SCOPE_MISSING) sind
        // durch — das hier ist der einzige Punkt, der Popup- UND Vollbild-Flow
        // genau einmal abdeckt (redeemStartedRef verhindert jeden zweiten
        // Einlauf). GA4 sendet per Beacon, das window.close()/replace() gleich
        // danach uebersteht der Versand.
        //
        // Das Ereignis heisst seit Feld C `google_account_connected` und nicht
        // mehr `feed_connected`. Gemessen wird hier ausschliesslich der
        // OAuth-Erfolg: ob das gespeicherte Konto ueberhaupt ein Merchant
        // Center traegt, ist an dieser Stelle unbekannt, `merchant_centers`
        // fuellt erst `fetch-account-data` spaeter. Den echten
        // Merchant-Center-Anschluss meldet `useConversionMilestones` weiterhin
        // als `feed_connected`.
        trackConversion('google_account_connected');
        if (window.opener) {
          window.opener.postMessage({
            type: 'GOOGLE_OAUTH_SUCCESS',
            data
          }, window.location.origin);
          window.close();
        } else {
          window.location.replace('/google-accounts');
        }

      } catch (error) {
        console.error('OAuth callback error:', error);
        // Derselbe Grund wie im Fehlerzweig oben: der Marker ueberlebt
        // sonst den dritten Ausgang dieses Callbacks und schickt den
        // naechsten Connect an ein Ziel, das niemand gewaehlt hat.
        takeAdsConnectReturn();
        Sentry.captureException(error, { tags: { feature: 'auth', action: 'oauth-callback' } });
        
        if (window.opener) {
          window.opener.postMessage({
            type: 'GOOGLE_OAUTH_ERROR',
            error: error instanceof Error ? error.message : 'Unknown error'
          }, window.location.origin);
          window.close();
        } else {
          window.location.replace('/google-accounts?oauth=error');
        }
      }
    };

    handleCallback();
  }, [searchParams, t]);

  // Finding R3-3: nur der Vollbild-Pfad landet hier. Im Popup-Pfad ist das Fenster
  // laengst geschlossen und der Toast des Openers zeigt denselben Text.
  if (failure) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-heading font-bold text-xl text-foreground">{failure.title}</h1>
          <p className="text-muted-foreground text-sm">{failure.description}</p>
          {/* Zielt auf "/", nicht auf /auth oder /google-accounts: die Route ist
              geschuetzt und leitet Nicht-Angemeldete selbst auf /auth. Damit stimmt der
              Link im Anmelde- UND im Verbinden-Flow, ohne dass diese Seite raten muss,
              in welchem sie gerade war. */}
          <a
            href="/"
            className="inline-block text-sm font-medium text-primary underline underline-offset-4"
          >
            {t('oauth.backToApp')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Spinner size="lg" />
        <p className="text-muted-foreground">{t('oauth.connecting')}</p>
      </div>
    </div>
  );
};

export default GoogleCallback;
